import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Accounts as MeteorAccounts } from 'meteor/accounts-base';
import { beginImpersonation } from '/imports/impersonation';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { defer, queryStringToObject } from '/imports/utils';
import { getUser, listActiveUsers, userDisplayName } from '/imports/api/users';
import { FormField } from '/imports/ui/FormField';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { RichSelect } from '/imports/ui/RichSelect';
import { Button } from '/imports/ui/Button';
import { Checkbox } from '/imports/ui/Checkbox';
import { AccountCredentials } from '/imports/ui/AccountCredentials';
import { Modal } from '/imports/ui/Modal';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { NotFoundPage } from '/imports/ui/NotFoundPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { autoForm, formConfig } from '/imports/ui/AutoForm';
import { Orgs } from '/imports/api/orgs';
import {
  canAdminAccounts, createAccount, Account,
  updateAccount, accountDisplayName, getAccount, deleteAccount, getAccountUser, getAccountLoginJwt
} from '/imports/api/accounts';

const AccountPageStyled = styled(Page)`
  .roles {
    display: flex;
    flex-direction: column;
    /* Claim the whole form row. As a plain flex item it sized to its content, which left the grid below a
       200px column to lay 14 roles out in — one per line down the page. */
    flex: 1 1 100%;
    h2 {
      margin-bottom: 0.5rem;
    }
  }
  .credentials {
    margin-top: 3rem;
  }
  .credentials-modal {
    .modal-body {
      padding-top: 2rem;
      form {
        padding-bottom: 0;
      }
    }
  }
  /* Thirteen roles on one non-wrapping flex row ran off the side of a narrow window. A grid rather than a
     wrapped flex row, so they land in aligned columns and a name can be found by scanning down one.
     min() in the track keeps a container narrower than one column from overflowing: the track collapses
     to the container instead of holding 13rem open. */
  .roles-checkboxes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(13rem, 100%), 1fr));
    gap: 0.5rem 1.5rem;
  }
  /* Centred, so the box stays level with a label that has wrapped to a second line. */
  .roles-checkboxes label { align-items: center; }
`;

/** Create/edit form for an account, including roles and credentials. */
export const AccountPage = () => {
  const currentUser = useUser();
  const navigate = useNavigate();
  const { _id } = useParams<any>();
  const isNew = _id === 'new';
  const query = queryStringToObject();
  const queryUserId = query?.userId;
  const [queryUser, loadingQueryUser] = queryUserId ? useMethod(getUser, queryUserId, [queryUserId]) : [undefined, false];
  const [update, setUpdate] = useState<Partial<Account>>(isNew ? { _id, active: true, orgId: currentUser?.orgId, userId: queryUserId } : { _id });
  useEffect(() => {
    if (!queryUser) return;
    toast.info('Configure the user\'s Account in the current Org');
    const { firstName, lastName, email, phone } = queryUser;
    queryUser && setUpdate({ ...update, userId: queryUser._id, firstName, lastName, email, phone });
  }, [queryUser?._id]);
  const orgId = currentUser?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  useEffect(() => { if (isNew) toast.info(<span>{strings.CreatingAccountInOrg()}:<br /><b>{org?.name || 'UNDEFINED'}</b></span>); }, []);
  const [showAccountCredentials, setShowAccountCredentials] = useState(false);
  /** Logs in as this account's user, in this account's org, via a one-time JWT. */
  const impersonate = async () => {
    const jwt = await getAccountLoginJwt.call(_id as string);
    // Before the login: it replaces the super user's own token in storage, and that token is the way back.
    beginImpersonation({ label: (document && accountDisplayName(document)) || _id || '', returnPath: `/accounts/${_id}` });
    MeteorAccounts.callLoginMethod({ methodArguments: [{ jwt }] });
    navigate('/');
  };
  const [document, loadingDocument] = isNew ? [undefined, false] : useMethod(getAccount, _id as string);
  const [user, loadingUser] = isNew ? [undefined, false] : useMethod(getAccountUser, _id as string);
  const [initializing, setInitializing] = useState(true);
  const initialized = currentUser && !loadingDocument && !loadingUser;
  const form = formConfig<Account>([
    [{ field: 'active', input: { type: Checkbox } }],
    [
      currentUser?.isSuperUser ? {
        field: 'userId', label: strings.User(), input: (setUpdate, update, _, error) => (
          <FormField key="userId" field="userId" error={error}>
            <label>{strings.User()}</label>
            <RichSelect
              id="acct-user"
              listMethod={listActiveUsers}
              value={update.userId || document?.userId || queryUserId}
              preSelectedOption={user ? { value: user._id, label: `${userDisplayName(user)} (${user.username})` } : undefined}
              onChange={(option) => {
                const _update: Partial<Account> = { userId: option.value };
                if (!update.firstName) _update.firstName = option.data.firstName;
                if (!update.lastName) _update.lastName = option.data.lastName;
                if (!update.email) _update.email = option.data.email;
                if (!update.phone) _update.phone = option.data.phone;
                setUpdate({ ...update, ..._update });
              }}
            />
          </FormField>
        )
      } : null,
      { field: 'firstName' }, { field: 'lastName' }
    ],
    [{ field: 'email' }, { field: 'phone', input: { type: PhoneInput } }],
    [{
      field: 'roles', input: (setUpdate, update, document, error) => (
        <div key="roles-div" className="roles">
          <h2>{strings.Roles()}</h2>
          <FormField key="roles" className="checkbox roles-checkboxes" field="roles" error={error}>
            <RoleCheckbox role="admin" label={strings.Admin()} document={document} update={update} setUpdate={setUpdate} />
            <RoleCheckbox role="generate" label={strings.Generate()} document={document} update={update} setUpdate={setUpdate} />
            <RoleCheckbox role="productionMeters" label={strings.ProductionMeters()} document={document} update={update} setUpdate={setUpdate} />
            <RoleCheckbox role="consumptionMeters" label={strings.ConsumptionMeters()} document={document} update={update} setUpdate={setUpdate} />
            <RoleCheckbox role="utilityAccounts" label={strings.UtilityAccounts()} document={document} update={update} setUpdate={setUpdate} />
            {currentUser?.isSuperUser && <RoleCheckbox role="prospector" label={strings.RoleProspector()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="ricExecutive" label={strings.RoleRicExecutive()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="calpine" label={strings.RoleCalpine()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="projectManagement" label={strings.ProjectManagement()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="pmDocumentApprover" label={strings.RolePmDocumentApprover()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="accounting" label={strings.RoleAccounting()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="invoiceApprover" label={strings.RoleInvoiceApprover()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="inventory" label={strings.Inventory()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="documents" label={strings.DocumentManagement()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="resigen" label={strings.ResiGen()} document={document} update={update} setUpdate={setUpdate} />}
            {currentUser?.isSuperUser && <RoleCheckbox role="luminia" label={strings.RoleLuminia()} document={document} update={update} setUpdate={setUpdate} />}
          </FormField>
        </div>
      )
    }],
  ], [user]);
  const [AccountForm] = autoForm({
    document,
    createMethod: createAccount as any,
    updateMethod: updateAccount,
    deleteMethod: deleteAccount,
    form,
    update,
    setUpdate,
    redirect: '/accounts'
  });
  useEffect(() => {
    initialized && form && defer(() => setInitializing(false));
  }, [form, initialized]);
  if (initializing || loadingQueryUser) return <LoadingFlex />;
  if (!canAdminAccounts(currentUser, document?.orgId)) return <NotPermittedPage />;
  if (!isNew && !document) return <NotFoundPage />;
  return (
    <AccountPageStyled className={`page${!initialized ? ' initializing' : ''}`}
      breadcrumbs={[{ path: '/accounts', label: strings.Accounts() }, { path: `/accounts/${_id}`, label: document && accountDisplayName(document) || strings.New() }]}
    >
      <PageHeading
        left={
          <h1>
            {isNew ? strings.NewAccount() : document && accountDisplayName(document)}
          </h1>
        }
        right={!isNew && currentUser?.isSuperUser && document?.userId
          ? <Button className="btn primary impersonate" onClick={() => impersonate()}>{strings.Impersonate()}</Button>
          : undefined}
      />
      <PageBody>
        {AccountForm}
      </PageBody>
      {
        showAccountCredentials ?
          (
            <Modal
              className="credentials-modal"
              title={strings.SetUsernameOrPassword()}
              onClose={() => setShowAccountCredentials(false)}
            >
              <AccountCredentials accountId={_id || 'new'} onSuccess={() => {
                toast.success(strings.PasswordSuccessfullyChanged());
                setShowAccountCredentials(false);
              }} />
            </Modal>
          )
          :
          null
      }
    </AccountPageStyled>
  );
};

interface RoleCheckboxProps {
  role: string;
  document?: Account;
  update: Partial<Account>;
  setUpdate: ((value: React.SetStateAction<Partial<Account>>) => void) | undefined;
  label: string;
}

/** Checkbox that toggles a single role on the account update. */
const RoleCheckbox = (props: RoleCheckboxProps) => {
  const { role, document, update, setUpdate, label } = props;
  return (
    <label>
      <Checkbox
        checked={!!(update.roles?.includes(role) ?? document?.roles?.includes(role))}
        onChange={(checked) => {
          let roles;
          if (checked) roles = [...(update.roles || document?.roles || []), role];
          else roles = (update.roles || document?.roles || []).filter((r) => r !== role);
          setUpdate?.({ ...update, roles });
        }}
      />
      {label}
    </label>
  );
};